import { useState, useEffect } from 'react';
import { txApi } from '../txApi.js';
import { C, Err, Spinner, SectionHeader, Badge } from './_shared.jsx';
import { useWorkspace } from '../../../shared/workspace/WorkspaceContext.jsx';

const ACTION_LABELS = {
  campaign_level_saved:        'Saved campaign rows',
  campaign_group_created:      'Campaign group created',
  campaign_group_deleted:      'Campaign group deleted',
  campaign_group_renamed:      'Campaign group renamed',
  campaign_group_restored:     'Campaign group restored',
  campaign_level_restored:     'Campaign version restored',
  client_created:              'Client added',
  platform_enabled_for_client: 'Platforms linked',
  taxonomy_structure_updated:  'Taxonomy updated',
  field_added:                 'Field added',
  field_updated:               'Field updated',
  field_deleted:               'Field deleted',
  fields_imported:             'Fields imported',
  campaign_created:            'Campaign row added',
  user_access_granted:         'User access granted',
  role_changed:                'Role changed',
};

function describeDetail(action, details) {
  if (!details || !Object.keys(details).length) return null;
  const d = details;
  switch (action) {
    case 'campaign_level_saved': {
      // One entry covers a whole editing session (the grid autosaves), so say how many saves
      // it stands for rather than pretending it was a single one.
      const saves = Number(d.save_count) || 1;
      return `${d.row_count ?? ''} row${d.row_count !== 1 ? 's' : ''} saved for ${d.level || ''} level`
        + `${d.group_name ? ` — "${d.group_name}"` : ''}`
        + (saves > 1 ? ` (${saves} saves)` : '');
    }
    case 'campaign_group_created':
    case 'campaign_group_deleted':
      return `"${d.name}"`;
    case 'client_created':
      return `"${d.name}"`;
    case 'platform_enabled_for_client':
      return `${(d.platform_ids || []).length} platform(s) linked`;
    case 'taxonomy_structure_updated':
      return `${d.level ? `${d.level} level` : ''}${d.type ? ` (${d.type})` : ''}`.trim() || null;
    case 'field_added':
    case 'field_updated':
    case 'field_deleted':
      return `"${d.name}"`;
    case 'fields_imported':
      return `${d.imported ?? 0} field${d.imported === 1 ? '' : 's'} added to ${d.level || 'a level'}`
        + (d.skipped ? `, ${d.skipped} already defined` : '');
    default:
      return null;
  }
}

function Avatar({ name }) {
  const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', background: C.s3,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 600, color: C.dim, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

export default function ActivityPage({ me }) {
  // The log is read in the active workspace, like every other page in the module. The client
  // filter this replaces defaulted to "All clients", so the first thing the page showed was
  // activity from clients the rest of the module was not scoped to.
  const { clientId, client: wsClient } = useWorkspace();

  const [activity, setActivity] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [err,      setErr]      = useState(null);

  useEffect(() => {
    // No workspace means no scope, and the endpoint reads a blank client_id as "every client".
    // Showing nothing is right here; showing everything is the bug this page had.
    if (!clientId) { setActivity([]); setLoading(false); return; }

    let alive = true;
    setLoading(true); setErr(null);
    txApi.getActivity({ client_id: clientId })
      .then(d => { if (alive) setActivity(d.activity); })
      .catch(e => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clientId]);

  return (
    <div>
      <SectionHeader title={`Activity Log${wsClient ? ` · ${wsClient.name}` : ''}`} />
      <Err msg={err} />

      {loading ? <Spinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {activity.length === 0 && (
            <div className="o-state" style={{ padding: 40 }}>
              {clientId
                ? `No activity yet in ${wsClient?.name || 'this workspace'}.`
                : 'No workspace selected. Pick a client from the workspace selector in the top bar.'}
            </div>
          )}
          {activity.map(item => {
            const detail = describeDetail(item.action, item.details);
            return (
              <div key={item.id} style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                padding: '12px 0', borderBottom: `1px solid ${C.border}`,
              }}>
                <Avatar name={item.actor_fullname || item.actor_username} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, marginBottom: 3 }}>
                    <strong style={{ color: C.text }}>{item.actor_fullname || item.actor_username}</strong>
                    {' '}
                    <span style={{ color: C.dim }}>{ACTION_LABELS[item.action] || item.action}</span>
                    {item.client_name && <> in <Badge>{item.client_name}</Badge></>}
                  </div>
                  {detail && (
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{detail}</div>
                  )}
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                    {new Date(item.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
